export interface BillingInvoiceItem {
    type: 'tariff' | 'channel'
    title: string
    price: number
    quantity: number
    tariffName?: string
}

export interface MultiSubscriptionInvoiceRow {
    kind: 'tariff' | 'slot'
    name: string | null
    totalRub: number
}

export interface PaymentLineItem {
    kind?: 'tariff' | 'slot'
    name?: string | null
    baseAmount?: number
    amount?: number
}

export const GENERIC_CHANNEL_SLOT_INVOICE_TITLE = 'Channel'

/** Пустой / универсальный слот (баннер «Купить канал», WA/TG/MAX/SMS), не конкретный мессенджер. */
export function isUnassignedChannelSlotName(value?: string | null): boolean {
    const normalized = String(value || '')
        .trim()
        .toLowerCase()
        .replace(/\s+/g, '')
    return normalized === 'wa/tg/max/sms'
}

export function buildInvoiceItemsFromPaymentContext(params: {
    mode: 'billing' | 'add-channel'
    billingSource?: 'multi-subscription' | 'single-tariff'
    totalToPay: number
    tariffName: string
    currentChannelName?: string
    channelKind?: string | null
    multiSubscriptionRows?: MultiSubscriptionInvoiceRow[]
    paymentLineItems?: PaymentLineItem[]
}): BillingInvoiceItem[] {
    if (params.mode === 'add-channel') {
        return [
            {
                type: 'channel',
                title: resolveAddChannelInvoiceTitle(params),
                price: normalizeAmount(params.totalToPay),
                quantity: 1,
            },
        ]
    }

    if (
        params.billingSource === 'multi-subscription' &&
        Array.isArray(params.paymentLineItems) &&
        params.paymentLineItems.length
    ) {
        return allocateItems(
            params.paymentLineItems
                .filter((row) => row.kind === 'tariff' || row.kind === 'slot')
                .map((row) => ({
                    type: row.kind === 'tariff' ? ('tariff' as const) : ('channel' as const),
                    title:
                        row.kind === 'tariff'
                            ? row.name || params.tariffName
                            : normalizeChannelTitle(row.name),
                    tariffName:
                        row.kind === 'tariff' ? row.name || params.tariffName : undefined,
                    baseAmount: normalizeAmount(
                        Number(
                            typeof row.baseAmount === 'number'
                                ? row.baseAmount
                                : row.amount || 0
                        )
                    ),
                })),
            normalizeAmount(params.totalToPay)
        )
    }

    const billableRows = (params.multiSubscriptionRows || []).filter(
        (row) => row.kind === 'tariff' || Number(row.totalRub || 0) > 0
    )

    if (billableRows.length <= 1) {
        return [
            {
                type: 'tariff',
                title: params.tariffName,
                tariffName: params.tariffName,
                price: normalizeAmount(params.totalToPay),
                quantity: 1,
            },
        ]
    }

    return allocateItems(
        billableRows.map((row) => ({
            type: row.kind === 'tariff' ? ('tariff' as const) : ('channel' as const),
            title:
                row.kind === 'tariff'
                    ? row.name || params.tariffName
                    : normalizeChannelTitle(row.name),
            tariffName:
                row.kind === 'tariff' ? row.name || params.tariffName : undefined,
            baseAmount: normalizeAmount(row.totalRub || 0),
        })),
        normalizeAmount(params.totalToPay)
    )
}

function allocateItems(
    items: Array<{
        type: 'tariff' | 'channel'
        title: string
        tariffName?: string
        baseAmount: number
    }>,
    totalToPay: number
): BillingInvoiceItem[] {
    const totalBase = items.reduce((sum, item) => sum + item.baseAmount, 0)

    if (!totalBase || totalToPay <= 0) {
        return items.map((item) => ({
            type: item.type,
            title: item.title,
            tariffName: item.tariffName,
            price: 0,
            quantity: 1,
        }))
    }

    const allocations = items.map((item, index) => {
        const raw = (item.baseAmount / totalBase) * totalToPay
        const floor = Math.floor(raw)

        return {
            ...item,
            index,
            price: floor,
            remainder: raw - floor,
        }
    })

    let remainder = totalToPay - allocations.reduce((sum, item) => sum + item.price, 0)

    allocations
        .sort((left, right) => {
            if (right.remainder !== left.remainder) {
                return right.remainder - left.remainder
            }

            return left.index - right.index
        })
        .forEach((item) => {
            if (remainder <= 0) return
            item.price += 1
            remainder -= 1
        })

    return allocations
        .sort((left, right) => left.index - right.index)
        .map((item) => ({
            type: item.type,
            title: item.title,
            tariffName: item.tariffName,
            price: item.price,
            quantity: 1,
        }))
}

function normalizeAmount(value: number): number {
    const normalized = Number(value || 0)
    if (!Number.isFinite(normalized)) return 0
    return Math.max(0, Math.round(normalized))
}

function resolveAddChannelInvoiceTitle(params: {
    currentChannelName?: string
    channelKind?: string | null
}): string {
    if (params.channelKind === 'bundle' || isUnassignedChannelSlotName(params.currentChannelName)) {
        return GENERIC_CHANNEL_SLOT_INVOICE_TITLE
    }
    return normalizeChannelTitle(params.currentChannelName)
}

function normalizeChannelTitle(value?: string | null): string {
    const raw = String(value || '').trim()
    const normalized = raw.toLowerCase()

    if (!normalized) return 'Channel'
    if (isUnassignedChannelSlotName(raw)) return GENERIC_CHANNEL_SLOT_INVOICE_TITLE
    if (normalized.includes('telegram')) return 'Telegram'
    if (normalized.includes('max')) return 'MAX'
    if (normalized.includes('whatsapp')) return 'WhatsApp'

    return raw
}
